/**
 * Purge Attachments API — thin client for the /api/purge-attachments
 * Cloudflare Pages Function.
 *
 * Submission-form attachments are working copies of CRM documents; the
 * server deletes the storage objects once a file reaches Awaiting
 * Signature (or later) and marks the file row (metadata is never deleted).
 *
 * Two of the three entry points here are FIRE-AND-FORGET: a purge failure
 * must never block a status change or affect pipeline load, so they never
 * throw — every error is swallowed and logged. The manual adviser purge
 * (`manualPurge`) DOES surface errors, because the adviser is waiting on a
 * confirmation and needs to know if it failed.
 */

import supabase from './supabaseClient';

const ENDPOINT = '/api/purge-attachments';

/**
 * Attach the caller's Supabase JWT so the server can authenticate. Returns
 * null if there is no session (the server will reject with 401, which the
 * fire-and-forget callers ignore).
 */
async function authHeaders() {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : null;
  } catch {
    return null;
  }
}

/**
 * Event-driven purge for a single file. Called (without await) right after
 * a file moves into Awaiting Signature. Never throws.
 *
 * @param {string} rowId — the files row id (PK). The server looks the row
 *   up by this id, then reads files.file_id off it to locate the storage
 *   folder. The `file_id` body key is this row id (server contract).
 */
export async function triggerPurge(rowId) {
  try {
    const headers = await authHeaders();
    if (!headers) return; // no session — nothing to do
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ file_id: rowId }),
    });
  } catch (e) {
    console.warn('[purge] triggerPurge failed (ignored):', e);
  }
}

/**
 * Backstop sweep over ALL eligible-but-unpurged files. Called once per app
 * load from the pipeline view. Never throws.
 */
export async function triggerBackstopSweep() {
  try {
    const headers = await authHeaders();
    if (!headers) return;
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({}),
    });
  } catch (e) {
    console.warn('[purge] backstop sweep failed (ignored):', e);
  }
}

/**
 * Adviser-only manual purge for a single file ("escape hatch"). Unlike the
 * fire-and-forget calls, this awaits and THROWS on failure so the caller
 * can show an error in the confirmation flow.
 *
 * @param {string} rowId — the files row id.
 * @param {string} author — the adviser's first name, for the system note.
 * @returns {Promise<{purged:number, failed:number}>}
 */
export async function manualPurge(rowId, author) {
  const headers = await authHeaders();
  if (!headers) throw new Error('You must be signed in to purge attachments.');
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ file_id: rowId, manual: true, author }),
  });
  if (!res.ok) {
    let msg = `Purge failed (${res.status})`;
    try {
      const data = await res.json();
      if (data.error) msg = data.error;
    } catch {
      /* non-JSON response — keep default */
    }
    throw new Error(msg);
  }
  return res.json();
}
